import { useState, useCallback } from 'react';
import { DEFAULT_GROSS_SALARY } from '../constants';
import { renderSuggestionTip } from '../utils/suggestions';
import { getCurrentTaxYearString, type TaxYearString } from '../utils/taxYear';
import type {
  BreakdownData,
  ChildInput,
  PersonInput,
  HouseholdInput,
  HouseholdReport,
  StrategyName,
} from '../types';

const DEFAULT_PERSON: PersonInput = {
  gross_salary: DEFAULT_GROSS_SALARY,
  salary_sacrifice_contributions: 0,
  taxable_benefits: 0,
  net_pension_contributions_percentage: 5, // 5%
  net_pension_contributions_amount: 5000, // £5000
  net_pension_contributions_type: 'percentage',
  pension_type: 'relief_at_source',
  employer_pension_contributions_percentage: 3, // 3%
  employer_pension_contributions_amount: 3000, // £3000
  employer_pension_contributions_type: 'percentage',
  payroll_giving_donations: 0,
  gift_aid_donations: 0,
  charity_type: 'gift_aid',
  tax_code: "1257L",
};

interface HouseholdState {
  primary: PersonInput;
  setPrimary: React.Dispatch<React.SetStateAction<PersonInput>>;
  includePartner: boolean;
  setIncludePartner: React.Dispatch<React.SetStateAction<boolean>>;
  partner: PersonInput;
  setPartner: React.Dispatch<React.SetStateAction<PersonInput>>;
  children: ChildInput[];
  setChildren: React.Dispatch<React.SetStateAction<ChildInput[]>>;
  claimsChildBenefit: boolean;
  setClaimsChildBenefit: React.Dispatch<React.SetStateAction<boolean>>;
  preferredStrategy: StrategyName | null;
  setPreferredStrategy: React.Dispatch<React.SetStateAction<StrategyName | null>>;
  taxYear: TaxYearString;
  setTaxYear: React.Dispatch<React.SetStateAction<TaxYearString>>;
  breakdown: BreakdownData[];
  warnings: string[];
  hasReport: boolean;
  hasChildren: boolean;
  updateFromReport: (report: HouseholdReport | null) => void;
  getHouseholdInput: () => HouseholdInput;
}

export function useHouseholdState(): HouseholdState {
  const [primary, setPrimary] = useState<PersonInput>(DEFAULT_PERSON);
  const [includePartner, setIncludePartner] = useState<boolean>(false);
  const [partner, setPartner] = useState<PersonInput>({ ...DEFAULT_PERSON, gross_salary: 0 });
  const [children, setChildren] = useState<ChildInput[]>([]);
  const [claimsChildBenefit, setClaimsChildBenefit] = useState<boolean>(false);
  const [preferredStrategy, setPreferredStrategy] = useState<StrategyName | null>(null);
  const [taxYear, setTaxYear] = useState<TaxYearString>(getCurrentTaxYearString);
  const [breakdown, setBreakdown] = useState<BreakdownData[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [hasReport, setHasReport] = useState<boolean>(false);

  const hasChildren = children.length > 0;

  // Update breakdown and warnings when report changes
  const updateFromReport = useCallback((report: HouseholdReport | null): void => {
    if (!report) {
      setHasReport(false);
      setBreakdown([]);
      setWarnings([]);
      return;
    }

    // Calculate total benefits (taxable + childcare currently secured - repayments)
    const securedFreeChildcare =
      report.free_childcare.type === 'eligible' || report.free_childcare.type === 'reduced'
        ? report.free_childcare.value
        : 0;
    const totalBenefits = report.taxable_benefits +
      (report.has_children ? securedFreeChildcare : 0) -
      report.child_benefit_repayment;

    const newBreakdown: BreakdownData[] = [
      { name: "Cash in Hand", value: report.cash_in_hand },
      ...(totalBenefits > 0 ? [{ name: "Benefits", value: totalBenefits }] : []),
      { name: "Income Tax", value: report.actual_tax - report.annual_allowance_charge },
      { name: "National Insurance", value: report.national_insurance },
      ...(report.annual_allowance_charge > 0 ? [{ name: "Annual Allowance Charge", value: report.annual_allowance_charge }] : []),
      { name: "Charity with Gift-Aid", value: report.charity_gross_donation },
      { name: "Pension inc. HMRC top-up", value: report.pension_contributions_grossed_up },
    ];
    setBreakdown(newBreakdown);

    const msgs: string[] = [];
    if (report.rebate_due > 0) {
      msgs.push(`You're due a rebate of £${report.rebate_due.toFixed(2)}`);
    }
    if (report.free_childcare.type === 'lost') {
      const lost = report.free_childcare.lost;
      const valueText = lost > 0
        ? ` (worth ~£${Math.round(lost).toLocaleString()}/year)`
        : '';
      msgs.push(`⚠ You'll lose free childcare eligibility${valueText}`);
    } else if (report.free_childcare.type === 'reduced') {
      const lost = report.free_childcare.lost;
      const valueText = lost > 0
        ? ` (lose ~£${Math.round(lost).toLocaleString()}/year of income-tested hours; universal 15h remains)`
        : ' (income-tested hours lost; universal 15h remains)';
      msgs.push(`⚠ Free childcare reduced${valueText}`);
    }
    if (report.tax_free_childcare.type === 'lost' && report.tax_free_childcare.lost > 0) {
      msgs.push(`⚠ You'll lose Tax-Free Childcare (worth up to £${Math.round(report.tax_free_childcare.lost).toLocaleString()}/year)`);
    }
    if (!report.can_contribute_more_to_sipp) {
      msgs.push("⚠ You've maxed out your SIPP allowance");
    }
    msgs.push(...report.suggestion_to_reduce_tax.map(renderSuggestionTip));
    setWarnings(msgs);
    setHasReport(true);
  }, []);

  // Generate household input payload
  const getHouseholdInput = useCallback((): HouseholdInput => {
    return {
      primary,
      partner: hasChildren && includePartner ? partner : undefined,
      children,
      claims_child_benefit: hasChildren ? claimsChildBenefit : undefined,
      preferred_strategy: preferredStrategy ?? null,
      tax_year: taxYear,
    };
  }, [primary, partner, children, claimsChildBenefit, hasChildren, includePartner, preferredStrategy, taxYear]);

  return {
    // State
    primary,
    setPrimary,
    includePartner,
    setIncludePartner,
    partner,
    setPartner,
    children,
    setChildren,
    claimsChildBenefit,
    setClaimsChildBenefit,
    preferredStrategy,
    setPreferredStrategy,
    taxYear,
    setTaxYear,
    breakdown,
    warnings,
    hasReport,
    hasChildren,

    // Functions
    updateFromReport,
    getHouseholdInput,
  };
}
